import { lcdText } from "../lib/format";
import { Wordmark } from "./Wordmark";

type LcdProps = {
  lines: string[];
  size?: "sm" | "md";
  /** Announce changes to screen readers (e.g. "MAYAS-SHOP IS ONLINE"). */
  live?: boolean;
  className?: string;
};

/** The LCD panel: VT323 on pager green, upper case, with a blinking cursor after the last line. */
export function Lcd({ lines, size = "md", live, className }: LcdProps) {
  const text = lines.map(lcdText);
  return (
    <div
      className={["lcd", size === "sm" ? "sm" : "", className ?? ""].filter(Boolean).join(" ")}
      aria-live={live ? "polite" : undefined}
      data-testid="lcd"
    >
      {/* Screen readers get one sentence; the visual keeps the pager line breaks and cursor. */}
      <span className="sr-only">{text.join(" ")}</span>
      <span aria-hidden="true">
        {text.join("\n")}
        <span className="cursor">_</span>
      </span>
    </div>
  );
}

/**
 * The status light in the pager's centre key. One meaning everywhere, web and iOS:
 * red = needs you now (slow pulse), amber = watching / told you, green = all good, off = quiet.
 * Colour is never the only signal: the LCD text always says the state.
 */
export type Tone = "red" | "amber" | "green" | "off";

/** The signature: a pager device around an LCD, with the status light. At most one per screen. */
export function Pager({
  brand,
  lines,
  tone = "green",
  size = "md",
  live,
  hotKey,
}: {
  /** Another maker's label (the 1996 pager on the landing page); ours is the engraved wordmark. */
  brand?: string;
  lines: string[];
  tone?: Tone;
  size?: "sm" | "md";
  live?: boolean;
  /** @deprecated use tone="red" */
  hotKey?: boolean;
}) {
  const t: Tone = hotKey ? "red" : tone;
  return (
    <div className={`pager tone-${t}${size === "sm" ? " sm" : ""}`} data-tone={t}>
      <div className="brand" aria-hidden="true">
        {brand ?? <Wordmark variant="engraved" />}
      </div>
      <Lcd lines={lines} live={live} />
      <div className="keys" aria-hidden="true">
        <span />
        <span className="led" />
        <span />
      </div>
    </div>
  );
}

/** The pager in miniature for the top bar: the light plus one LCD line. */
export function PagerChip({ tone, text }: { tone: Tone; text: string }) {
  return (
    <span className={`pchip tone-${tone}`} data-tone={tone}>
      <span className="pchip-led" aria-hidden="true" />
      <span className="pchip-lcd">{lcdText(text)}</span>
    </span>
  );
}
